import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useLocalization } from '@ui-framework/index';

const CONTROL_HEIGHT = 34;
const identityOptions = options => options;

// Shared compact control sizing so sibling selects (e.g. plain MUI selects
// rendered next to this autocomplete in dialogs) match its height exactly.
export const compactControlSx = {
  '& .MuiInputBase-root': {
    minHeight: CONTROL_HEIGHT,
  },
  '& .MuiAutocomplete-inputRoot': {
    py: '0 !important',
  },
  '& .MuiSelect-select': {
    py: '5.5px',
  },
};

const inputSx = compactControlSx;

function useDebouncedValue(value, delay = 300) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => window.clearTimeout(timeoutId);
  }, [delay, value]);

  return debouncedValue;
}

function getResultItems(result) {
  if (!result?.ok) {
    return [];
  }

  return result.data?.data || [];
}

function SxclServicesAutocomplete({
  dataProvider,
  endpoint,
  label,
  value,
  onChange,
  getOptionLabel,
  buildParams,
  buildValueParams,
  required = false,
  disabled = false,
  noOptionsText,
  placeholder,
  minSearchLength = 0,
  searchPromptText,
  transformOptions = identityOptions,
  renderOption,
}) {
  const { formatMessage } = useLocalization();
  const resolvedNoOptionsText =
    noOptionsText ??
    formatMessage({ id: 'NO_RESULTS', defaultMessage: 'No results' });
  const resolvedPlaceholder =
    placeholder ??
    formatMessage({ id: 'PLEASE_SELECT', defaultMessage: 'Please select' });
  const resolvedSearchPromptText = searchPromptText ?? resolvedNoOptionsText;
  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const displayedValueRef = useRef(null);
  const optionsRequestIdRef = useRef(0);
  const selectedOptionRequestIdRef = useRef(0);
  const selectedOptionRef = useRef(null);
  selectedOptionRef.current = selectedOption;
  const debouncedSearchValue = useDebouncedValue(searchValue);

  const selectedValue = useMemo(() => {
    if (!value) {
      return null;
    }

    return (
      (selectedOption?.id === value ? selectedOption : null) ||
      options.find(option => option.id === value) || {
        id: value,
        label: value,
      }
    );
  }, [options, selectedOption, value]);

  const loadOptions = useCallback(async () => {
    const requestId = optionsRequestIdRef.current + 1;
    optionsRequestIdRef.current = requestId;
    const normalizedSearchValue = debouncedSearchValue.trim();

    if (!dataProvider || normalizedSearchValue.length < minSearchLength) {
      setOptions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    let result;
    try {
      result = await dataProvider.get(
        endpoint,
        buildParams(normalizedSearchValue),
      );
    } catch {
      result = null;
    }

    if (requestId !== optionsRequestIdRef.current) {
      return;
    }

    setLoading(false);
    setOptions(transformOptions(getResultItems(result)));
  }, [
    buildParams,
    dataProvider,
    debouncedSearchValue,
    endpoint,
    minSearchLength,
    transformOptions,
  ]);

  useEffect(() => {
    loadOptions();

    return () => {
      optionsRequestIdRef.current += 1;
    };
  }, [loadOptions]);

  const loadSelectedOption = useCallback(async () => {
    if (
      !value ||
      !dataProvider ||
      !buildValueParams ||
      selectedOptionRef.current?.id === value
    ) {
      return;
    }

    const requestId = selectedOptionRequestIdRef.current + 1;
    selectedOptionRequestIdRef.current = requestId;
    let result;
    try {
      result = await dataProvider.get(endpoint, buildValueParams(value));
    } catch {
      result = null;
    }

    if (requestId !== selectedOptionRequestIdRef.current) {
      return;
    }

    const resolvedOption = transformOptions(getResultItems(result)).find(
      option => option.id === value,
    );
    if (resolvedOption) {
      setSelectedOption(resolvedOption);
      setInputValue(getOptionLabel(resolvedOption));
      displayedValueRef.current = value;
    }
  }, [
    buildValueParams,
    dataProvider,
    endpoint,
    getOptionLabel,
    transformOptions,
    value,
  ]);

  useEffect(() => {
    loadSelectedOption();

    return () => {
      selectedOptionRequestIdRef.current += 1;
    };
  }, [loadSelectedOption]);

  useEffect(() => {
    if (!value) {
      setSelectedOption(null);
      displayedValueRef.current = null;
      // Clearing the value externally (form clear/reset or the clear icon)
      // must also clear the visible input text.
      setInputValue('');
      setSearchValue('');
      return;
    }

    if (selectedOption?.id && selectedOption.id !== value) {
      setSelectedOption(null);
    }
  }, [selectedOption?.id, value]);

  useEffect(() => {
    if (!value) {
      return;
    }

    if (selectedOption?.id === value) {
      displayedValueRef.current = value;
      return;
    }

    if (displayedValueRef.current === value) {
      return;
    }

    const resolvedOption = options.find(option => option.id === value);
    if (resolvedOption) {
      setInputValue(getOptionLabel(resolvedOption));
      displayedValueRef.current = value;
    }
  }, [getOptionLabel, options, selectedOption?.id, value]);

  return (
    <Autocomplete
      size="small"
      options={options}
      value={selectedValue}
      inputValue={inputValue}
      loading={loading}
      disabled={disabled}
      noOptionsText={
        inputValue.trim().length < minSearchLength
          ? resolvedSearchPromptText
          : resolvedNoOptionsText
      }
      getOptionLabel={option => {
        if (!option) {
          return '';
        }

        return getOptionLabel(option);
      }}
      isOptionEqualToValue={(option, nextValue) => option.id === nextValue.id}
      {...(renderOption ? { renderOption } : {})}
      onInputChange={(_event, nextInputValue, reason) => {
        if (
          reason === 'reset' &&
          buildValueParams &&
          nextInputValue === value &&
          selectedOption?.id !== value
        ) {
          return;
        }
        setInputValue(nextInputValue);
        if (reason === 'input' || reason === 'clear') {
          setSearchValue(nextInputValue);
        }
      }}
      onChange={(_event, option) => {
        setSelectedOption(option);
        onChange(option?.id || '');
      }}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          required={required}
          placeholder={resolvedPlaceholder}
          sx={inputSx}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={16} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

export default SxclServicesAutocomplete;
